import i18n from "i18next";
import { faPaperclip } from "@fortawesome/pro-regular-svg-icons";
import { AavoObjectTypeApi } from "src/api/generated/common/aavoObject/aavoObjectTypeApi.ts";
import { DocumentsOfSingleObjectView } from "src/components/views/documents/objectDocuments/DocumentsOfSingleObjectView.tsx";
import { AsyncButton } from "../../../common/buttons/AsyncButton.tsx";
import type { AsyncButtonProps } from "../../../common/buttons/AsyncButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { AavoObjectRef } from "src/api/generated/common/aavoObject/aavoObjectRef.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { DocumentsViewEditable } from "src/components/views/documents/types.ts";
import React from "react";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface DocumentsOfObjectButtonProps extends Omit<AsyncButtonProps, "onClick"> {
	objectRef: AavoObjectRef | undefined;
	editable?: DocumentsViewEditable;
	dialogTitle?: (objectDescription: string) => string;
	dialogContent?: () => React.ReactNode;
}

export const DocumentsOfObjectButton = ({
	objectRef,
	label = "",
	icon = faPaperclip,
	tooltip = i18n.t("connected_documents"),
	editable,
	dialogTitle,
	dialogContent,
	disabled: disabledProp,
	...other
}: DocumentsOfObjectButtonProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const { openDialog } = useGenericDialog();

	if (!tenantConfig.documentsEnabled) return;

	const disabled = disabledProp ?? !objectRef;

	return (
		<AsyncButton
			label={label}
			icon={icon}
			disabled={disabled}
			tooltip={tooltip}
			color={"primary"}
			onClick={async () => {
				if (!objectRef) return;

				const description = await AavoObjectTypeApi.getAavoObjectDescription({
					objectRef: objectRef,
				});

				openDialog(() => ({
					title:
						dialogTitle ?
							dialogTitle(description)
						:	`${i18n.t("connected_documents")} (${description})`,
					agentContext: {
						key: defineAgentUiContextKey("documents.objectDocuments.dialog"),
						pathSegments: [i18n.t("connected_documents")],
						data: {
							objectType: objectRef.objectType,
							objectId: objectRef.objectId ?? null,
						},
					},
					content:
						dialogContent != null ? dialogContent() : (
							<DocumentsOfSingleObjectView objectRef={objectRef} editable={editable} />
						),
				}));
			}}
			{...other}
		/>
	);
};
